import { SetStateAction, useCallback, useMemo, useState } from 'react';
import { Column, SortDirection } from '@robocorp/components';
import { parseFilterTimeRange, stringifyFilterTimeRange } from '~/utils/timeRangeHelpers';
import { TimeRangeValues } from '~/constants/timeRanges';
import { useLocalStorage } from './useStorage';
import useDebounce from './useDebounce';
import { useQuerySearch } from './useQuerySearch';

// keep all filter settings keys in one place for usage visibility
enum FilterSettingsKeys {
  OrganizationAuditLogs = 'organizationAuditLogs',
  Webhooks = 'webhooks',
  WebhooksLogs = 'webhooksLogs',
  ApiKeys = 'apiKeys',
  Assets = 'assets',
  AssetLogs = 'assetLogs',
  ProcessRuns = 'processRuns',
  ProcessesPerformance = 'performance',
  Processes = 'processes',
  StepRuns = 'stepRuns',
  WorkItems = 'workItems',
  workspaceAuditLogs = 'workspaceAuditLogs',
  Vault = 'vault',
  OnDemandWorkers = 'onDemandWorkers',
  SelfHostedWorkers = 'selfHostedWorkers',
  WorkerGroups = 'workerGroups',
  TaskPackages = 'taskPackages',
  WorkItemEventLog = 'workItemEventLog',
  activityProcessRuns = 'activityProcessRuns',
  activityScheduleRuns = 'activityScheduledRuns',
  activityStepRunsRuns = 'activityStepRuns',
  activityAssistantRuns = 'activityAssistantRuns',
  userSettings = 'userSettings',
}

type KeyType = `${FilterSettingsKeys}` | (string & {});

export const FILTER_SETTINGS_SIZES = [10, 25, 50, 100, 200] as const;
const internalDefaults = {
  selected: [],
};
const paginationDefaults = {
  page: 0,
  cursor: null,
};
const defaults = {
  columns: [],
  search: '',
  filters: {},
  sort: null,
  resize: {},
  size: FILTER_SETTINGS_SIZES[0],
};

interface InternalSettings {
  selected: number[];
}

interface PersistedSettings {
  visibility: Record<string, boolean>;
  size: (typeof FILTER_SETTINGS_SIZES)[number];
  resize: Record<string, number>;
}

type FilterValue<T = any> = T;
type FilterRecord<T extends FilterValue[] = FilterValue[]> = Record<string, T>;
type OptionalFilterRecord<T = FilterValue[] | undefined> = Record<string, T>;

interface QuerySettings<T = FilterRecord> {
  sort: [string, SortDirection] | null;
  filters: T;
  search: string;
  page: number;
  cursor: string | null;
}

type QuerySettingsWithOptionalFilters = Omit<QuerySettings, 'filters'> & {
  filters: OptionalFilterRecord;
};

export type FilterSettings<T = FilterRecord> = {
  columns?: Column[];
} & Partial<InternalSettings> &
  Partial<Omit<PersistedSettings, 'visibility'>> &
  Partial<QuerySettings<T>>;

/**
 * Read, set or update filter settings.
 *
 * @example
 * const { columnVisibility, onColumnVisibilityChange } = useFilterSettings('foo' { initialValue: { columns: [] } });
 */
export const useFilterSettings = <T extends FilterRecord>(
  key: KeyType,
  {
    columns = defaults.columns,
    page: initialPage = paginationDefaults.page,
    cursor: initialCursor = paginationDefaults.cursor,
    selected: initialSelected = internalDefaults.selected,
    search: initialSearch = defaults.search,
    filters: initialFilters = defaults.filters as T,
    sort: initialSort = defaults.sort,
    resize: initialResize = defaults.resize,
    size: initialSize = defaults.size,
  }: FilterSettings<T> = {},
) => {
  const [{ selected }, setInternalSettings] = useState<InternalSettings>({
    selected: initialSelected,
  });

  const setInternalSetting = useCallback(
    <K extends keyof InternalSettings>(settingKey: K) =>
      (state: SetStateAction<InternalSettings[K]>) =>
        setInternalSettings((prevState) => ({
          ...prevState,
          selected: internalDefaults.selected,
          [settingKey]: state instanceof Function ? state(prevState[settingKey]) : state,
        })),
    [],
  );

  const querySettings = useMemo(
    () => ({
      search: initialSearch,
      filters: initialFilters,
      sort: initialSort,
      page: initialPage,
      cursor: initialCursor,
    }),
    [],
  );

  const [queryValue = {}, setQuerySettings] = useQuerySearch<Partial<QuerySettings<T>>>(key, {
    replace: true,
  });

  const filters = useMemo(() => {
    if (queryValue.filters) {
      const placeholderFilters: FilterRecord = {};
      Object.keys(querySettings.filters).forEach((key) => {
        placeholderFilters[key] = [];
      });
      return { ...placeholderFilters, ...queryValue.filters };
    }

    return querySettings.filters;
  }, [queryValue.filters, querySettings.filters]);

  const search = queryValue.search || querySettings.search;
  const sort = queryValue.sort || querySettings.sort;
  const page = Number(queryValue.page || querySettings.page);
  const cursor = queryValue.cursor || querySettings.cursor;
  const searchDebounced = useDebounce(search, 200);

  const setQuerySetting = useCallback(
    <K extends keyof QuerySettings<T>>(settingKey: K) =>
      (
        state:
          | QuerySettings[K]
          | ((state: QuerySettings<T>[K]) => QuerySettingsWithOptionalFilters[K]),
      ) => {
        setInternalSettings(internalDefaults);
        return setQuerySettings((prevState) => {
          const item = prevState?.[settingKey] ?? querySettings[settingKey];
          return {
            ...prevState,
            page: undefined,
            cursor: undefined,
            [settingKey]: (state instanceof Function ? state(item) : state) || undefined,
          };
        });
      },
    [],
  );

  const persistedSettings = useMemo<PersistedSettings>(() => {
    const visibility: PersistedSettings['visibility'] = {};
    columns.forEach((column) => {
      if (column.hidden !== undefined) {
        visibility[column.id] = column.required ? false : column.hidden;
      }
    });

    return {
      resize: initialResize,
      size: initialSize,
      visibility,
    };
  }, []);

  const {
    storageValue: { size, resize, visibility: columnVisibility },
    setStorageValue,
  } = useLocalStorage<PersistedSettings>({
    key,
    initialValue: persistedSettings,
    sync: true,
  });
  const columnsWithVisibility = useMemo<Column[]>(
    () => columns.map((column) => ({ ...column, hidden: columnVisibility?.[column.id] })),
    [columnVisibility],
  );

  const setPersistedSetting = useCallback(
    <K extends keyof PersistedSettings>(settingKey: K) =>
      (state: SetStateAction<PersistedSettings[K]>) =>
        setStorageValue((prevState) => ({
          ...prevState,
          [settingKey]: state instanceof Function ? state(prevState[settingKey]) : state,
        })),
    [],
  );

  const onSizeChange = useCallback(
    (value: PersistedSettings['size']) => {
      setInternalSettings(internalDefaults);
      setQuerySettings((previous) => ({ ...previous, ...paginationDefaults }));
      return setPersistedSetting('size')(value);
    },
    [setPersistedSetting, setQuerySettings],
  );

  const onPageChange = useCallback(
    (value: SetStateAction<QuerySettings<T>['page']>) => {
      setInternalSetting('selected')([]);
      return setQuerySettings(({ page: previousPage = querySettings.page, ...previous } = {}) => {
        const newPage = value instanceof Function ? value(Number(previousPage)) : value;
        return {
          ...previous,
          page: newPage > 0 ? newPage : undefined,
        };
      });
    },
    [setInternalSetting, setQuerySettings],
  );

  /**
   * Resets cursor when reaching first page
   */
  const onCursorChange = useCallback(
    (newCursor: string | null, type?: 'next' | 'previous') => {
      setInternalSetting('selected')([]);
      return setQuerySettings(({ page: previousPage = querySettings.page, ...previous } = {}) => {
        const newPage = type
          ? Number(previousPage) + (type === 'next' ? 1 : -1)
          : Number(previousPage);
        return {
          ...previous,
          cursor: newPage > 0 ? newCursor : undefined,
          page: newPage > 0 ? newPage : undefined,
        };
      });
    },
    [setInternalSetting, setQuerySettings],
  );

  const { onColumnVisibilityChange, onResizeChange } = useMemo(
    () => ({
      onColumnVisibilityChange: setPersistedSetting('visibility'),
      onResizeChange: setPersistedSetting('resize'),
    }),
    [setPersistedSetting],
  );

  const { onSearchChange, onFilterChange, onSortChange } = useMemo(
    () => ({
      onSearchChange: setQuerySetting('search'),
      onFilterChange: setQuerySetting('filters'),
      onSortChange: setQuerySetting('sort'),
    }),
    [setQuerySetting],
  );

  const { onSelectChange } = useMemo(
    () => ({
      onSelectChange: setInternalSetting('selected'),
    }),
    [setInternalSetting],
  );

  const { pageFrom, pageTo } = useMemo(() => {
    const from = page * size;
    return {
      pageFrom: from,
      pageTo: from + size,
    };
  }, [page, size]);

  return {
    columns: columnsWithVisibility,
    columnVisibility,
    onColumnVisibilityChange,
    resize,
    onResizeChange,
    page,
    pageFrom,
    pageTo,
    onPageChange,
    cursor,
    onCursorChange,
    size,
    onSizeChange,
    search,
    searchDebounced,
    onSearchChange,
    filters: filters as T,
    onFilterChange,
    sort,
    onSortChange,
    selected,
    onSelectChange,
  };
};

export type FilterSettingsState = ReturnType<typeof useFilterSettings>;

/**
 * Helper hook for dealing with time range and filter settings.
 * - parsed time range is calculated only once as it's meant as initial value
 */
export const useFilterTimeRangeSettings = (
  preset: TimeRangeValues,
  range?: { from?: string; to?: string } | undefined,
) => {
  const initialTimeRangeValue = useMemo(() => parseFilterTimeRange([preset, range]), []);
  return [initialTimeRangeValue, stringifyFilterTimeRange] as const;
};

export type TimeRangePeriod =
  | [TimeRangeValues]
  | [TimeRangeValues.CUSTOM, { from: string; to: string }]
  | [];
